import type {
  Hit,
  SearchResponse as AlgoliaSearchResponse,
} from "algoliasearch";
import { algoliasearch } from "algoliasearch";

const indexName =
  window.algoliaIndexName ?? import.meta.env.VITE_ALGOLIA_INDEX_NAME ?? "";
const appId =
  window.algoliaAppIdKey ?? import.meta.env.VITE_ALGOLIA_APP_ID ?? "";
const apiKey =
  window.algoliaApiKey ?? import.meta.env.VITE_ALGOLIA_API_KEY ?? "";

export const searchClient =
  appId && apiKey ? algoliasearch(appId, apiKey) : null;

export interface SearchRecord {
  publicationId: string;
  objectID: string; // Unique identifier for the record in algolia
  type: string;
  parentType?: string; // used when larger blocks have to be split to stay under index item limit e.g. blockquotes
  menuType: string;
  content: string;
  chapterId: string;
  chapterTitle?: string;
  sectionTitle?: string;
  subsectionTitle?: string;
  h2Title?: string;
  h3Title?: string;
  previousHtml?: string;
  nextHtml?: string;
  frjRecord?: boolean;
  url?: string;
}

export type SearchHit = Omit<
  Hit<SearchRecord>,
  "_highlightResult" | "_snippetResult"
> & {
  _highlightResult: {
    content?: {
      value: string;
      matchLevel: string;
      matchedWords: string[];
    };
  };
  _snippetResult: {
    content?: {
      value: string;
      matchLevel: string;
    };
  };
};

type SearchResponse = Omit<AlgoliaSearchResponse<SearchRecord>, "hits"> & {
  hits: SearchHit[];
};

export const searchBookFragments = async (
  searchTerm: string,
  filters?: string,
  facetFilters?: string[][],
) => {
  if (!searchClient) {
    throw new Error("Algolia search client is not configured");
  }
  const result = await searchClient.searchSingleIndex<SearchRecord>({
    indexName,
    searchParams: {
      filters,
      facetFilters,
      query: searchTerm,
      length: 5,
      hitsPerPage: 5,
      facets: ["publicationId"],
      removeStopWords: ["en"],
      attributesToSnippet: ["content:30"],
    },
  });
  // _highlightResult is very weirdly typed in algoliasearch and infinite
  // recursion is a problem. So we have to cast it to the correct type
  return result as SearchResponse;
};
